import { useState, useEffect, useCallback } from 'react';
import { AppAnnouncement } from '../models/AppAnnouncement';
import { AppAnnouncementService } from '../services/appAnnouncementService';

export const useAppAnnouncements = () => {
    const [announcements, setAnnouncements] = useState<AppAnnouncement[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchAnnouncements = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await AppAnnouncementService.getAllAnnouncements();
            console.log(data);
            setAnnouncements(data);
            setTotal(data?.length || 0);
        } catch (err) {
            setError('Failed to fetch announcements');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRecentAnnouncements = useCallback(async (limit: number = 5) => {
        setLoading(true);
        setError(null);
        try {
            const data = await AppAnnouncementService.getRecentAnnouncements(limit);
            return data;
        } catch (err) {
            setError('Failed to fetch recent announcements');
            console.error(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAnnouncementsPaginated = useCallback(
        async (page: number, limit: number, search?: string) => {
            setLoading(true);
            setError(null);
            try {
                const response = await AppAnnouncementService.getAnnouncementsPaginated(
                    page,
                    limit,
                    search
                );

                // Handle the response structure properly
                const announcementsData = response.announcements || [];
                const totalCount = response.total || 0;

                setAnnouncements(announcementsData);
                setTotal(totalCount);
                return { rows: announcementsData, total: totalCount };
            } catch (err) {
                setError('Failed to fetch announcements');
                console.error(err);
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const fetchAnnouncementById = async (id: number) => {
        setLoading(true);
        setError(null);
        try {
            const data = await AppAnnouncementService.getAnnouncementById(id);
            return data;
        } catch (err) {
            setError('Failed to fetch announcement by ID.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const addAnnouncement = async (
        newAnnouncement: Omit<AppAnnouncement, 'id' | 'created_time' | 'updated_time'>,
        file?: File
    ) => {
        try {
            const createdAnnouncementRes = await AppAnnouncementService.createAnnouncement(newAnnouncement, file);
            setAnnouncements(prev => [...prev, createdAnnouncementRes]);
            return createdAnnouncementRes;
        } catch (err) {
            setError('Failed to add announcement');
        }
    };

    const addAnnouncementWithMultipleImages = async (
        newAnnouncement: Omit<AppAnnouncement, 'id' | 'created_time' | 'updated_time'>,
        files: File[]
    ) => {
        try {
            const createdAnnouncementRes = await AppAnnouncementService.createAnnouncementWithMultipleImages(newAnnouncement, files);
            setAnnouncements(prev => [...prev, createdAnnouncementRes]);
            return createdAnnouncementRes;
        } catch (err) {
            setError('Failed to add announcement with multiple images');
        }
    };

    const updateAnnouncement = async (
        id: number,
        updatedAnnouncement: Omit<AppAnnouncement, 'id' | 'created_time' | 'updated_time'>,
        file?: File
    ) => {
        try {
            const updatedRes = await AppAnnouncementService.updateAnnouncement(id, updatedAnnouncement, file);
            setAnnouncements(prev => prev.map(announcement => (announcement.id === id ? updatedRes : announcement)));
            return updatedRes;
        } catch (err) {
            setError('Failed to update announcement');
            console.error(err);
        }
    };

    const updateAnnouncementWithMultipleImages = async (
        id: number,
        updatedAnnouncement: Omit<AppAnnouncement, 'id' | 'created_time' | 'updated_time'>,
        files: File[]
    ) => {
        try {
            const updatedRes = await AppAnnouncementService.updateAnnouncementWithMultipleImages(id, updatedAnnouncement, files);
            setAnnouncements(prev => prev.map(announcement => (announcement.id === id ? updatedRes : announcement)));
            return updatedRes;
        } catch (err) {
            setError('Failed to update announcement with multiple images');
            console.error(err);
        }
    };

    const deleteAnnouncement = async (id: number) => {
        try {
            await AppAnnouncementService.deleteAnnouncement(id);
            setAnnouncements(prev => prev.filter(announcement => announcement.id !== id));
        } catch (err) {
            setError('Failed to delete announcement');
            console.error(err);
        }
    };

    return {
        announcements,
        total,
        loading,
        error,
        fetchAnnouncements,
        fetchRecentAnnouncements,
        fetchAnnouncementsPaginated,
        fetchAnnouncementById,
        addAnnouncement,
        addAnnouncementWithMultipleImages,
        updateAnnouncement,
        updateAnnouncementWithMultipleImages,
        deleteAnnouncement
    };
};
